import { createContext, useContext } from 'react';

export type GuardState = { dirty: boolean; message: string };

export type GuardCtx = {
  /**
   * Register (or, with {@code null}, unregister) one component's unsaved-changes state.
   *
   * <p>{@code key} is that component's stable identity — one per hook instance. slice-3557
   * made this a keyed REGISTRY rather than a single active slot: pages and the components
   * they nest (a browse form view inside a browse page, a steps editor inside a rules page)
   * both register, and React runs child effects before parent ones, so with a single slot a
   * clean parent silently clobbered its dirty child's registration. The guard now prompts
   * while ANY registrant is dirty.
   */
  setActive: (key: object, state: GuardState | null) => void;
  ask: () => Promise<boolean>;
};

export const guardContext = createContext<GuardCtx | null>(null);

/**
 * Non-throwing form of {@link useGuardCtx}: returns {@code null} when no provider is mounted.
 *
 * <p>For SHARED infrastructure only (slice-3557 — {@code TableBrowserPage} wires every
 * genericCrud screen's dirty flag into the guard, and its 180+ component tests mount the page
 * without the app shell). Feature pages must keep using {@link useGuardCtx} so a page that
 * forgets the provider fails loudly instead of losing edits silently.
 */
export function useGuardCtxOptional(): GuardCtx | null {
  return useContext(guardContext);
}

export function useGuardCtx(): GuardCtx {
  const v = useContext(guardContext);
  if (!v) {
    throw new Error(
      'UnsavedChangesGuard hook called outside <UnsavedChangesGuardProvider>. Wrap the app shell.',
    );
  }
  return v;
}

export const DEFAULT_MESSAGE =
  'You have unsaved changes on this screen. Leaving will discard them. Continue?';
